import { initializeApp, getApps } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getStorage } from 'firebase/storage';

// App Check is required when Storage/Firestore enforcement is on. reCAPTCHA v3 fails on localhost, which
// breaks uploads. In development we use Firebase’s debug flow (see App Check docs): set
// FIREBASE_APPCHECK_DEBUG_TOKEN before init so the SDK issues a debug attestation instead of calling
// reCAPTCHA. Register the printed UUID once under Firebase Console → App Check → Manage debug tokens,
// or put that UUID in REACT_APP_APPCHECK_DEBUG_TOKEN. To skip App Check entirely (only if enforcement
// is off), set REACT_APP_DISABLE_APPCHECK=true.

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const freshmeetApp = initializeApp(firebaseConfig);

const appCheckDisabled = process.env.REACT_APP_DISABLE_APPCHECK === 'true';
if (!appCheckDisabled) {
  const isProd = process.env.NODE_ENV === 'production';
  if (!isProd && typeof window !== 'undefined') {
    const t = process.env.REACT_APP_APPCHECK_DEBUG_TOKEN;
    if (t && t !== 'true' && t !== '1') {
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = t;
    } else {
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
    }
    // eslint-disable-next-line no-console
    console.info(
      '[FreshMeet] App Check (dev): if uploads return 403, copy the “App Check debug token” from the console and add it in Firebase Console → App Check → Manage debug tokens (or set REACT_APP_APPCHECK_DEBUG_TOKEN).'
    );
  }

  initializeAppCheck(freshmeetApp, {
    provider: new ReCaptchaV3Provider('6Lf9LsgsAAAAAB_ujJ6xdClL5cv3ByIFKe-FAtKK'),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(freshmeetApp);
export const firestore = getFirestore(freshmeetApp);
export const storage = getStorage(freshmeetApp);
export const functions = getFunctions(freshmeetApp, 'us-central1');

if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true') {
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

const IDENTITY_APP_NAME = 'fury-identity';

/** Fury Identity web config (Auth issuer for Registration API + sign-in). */
export function isIdentityIntegrationEnabled() {
  return Boolean(
    process.env.REACT_APP_IDENTITY_FIREBASE_API_KEY &&
      process.env.REACT_APP_IDENTITY_FIREBASE_AUTH_DOMAIN &&
      process.env.REACT_APP_IDENTITY_FIREBASE_PROJECT_ID &&
      process.env.REACT_APP_IDENTITY_FIREBASE_APP_ID,
  );
}

function getOrInitIdentityApp() {
  const identityConfig = {
    apiKey: process.env.REACT_APP_IDENTITY_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_IDENTITY_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_IDENTITY_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_IDENTITY_FIREBASE_STORAGE_BUCKET || undefined,
    messagingSenderId: process.env.REACT_APP_IDENTITY_FIREBASE_MESSAGING_SENDER_ID || undefined,
    appId: process.env.REACT_APP_IDENTITY_FIREBASE_APP_ID,
  };
  const existing = getApps().find((a) => a.name === IDENTITY_APP_NAME);
  if (existing) {
    return existing;
  }
  return initializeApp(identityConfig, IDENTITY_APP_NAME);
}

/** Firebase Auth for Fury Identity; null when integration env is not set (legacy single-project mode). */
export const identityAuth = isIdentityIntegrationEnabled() ? getAuth(getOrInitIdentityApp()) : null;

/** Sign out Identity session when the dual-app integration is active. No-op in legacy mode. */
export async function signOutIdentityIfActive() {
  if (!identityAuth) {
    return;
  }
  const { signOut } = await import('firebase/auth');
  try {
    await signOut(identityAuth);
  } catch (e) {
    console.warn('signOutIdentityIfActive:', e.message);
  }
}

export default freshmeetApp;
